import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { formatCurrency } from '../utils/currency';
import ClickableMetric from '../components/ClickableMetric';
import {
  ChartBarIcon,
  CubeIcon,
  ScaleIcon,
  BanknotesIcon,
  InformationCircleIcon,
  PlusCircleIcon,
} from '@heroicons/react/24/outline';
import { useOrganization } from '../context/OrganizationContext';
import { getAuthHeaders } from '../utils/auth';
import { API_URL } from '../constants/urls';

interface ExtendedOrganization {
  id: string;
  name: string;
  currency?: string;
  description?: string;
  geographic_markets?: string[];
}

export default function Dashboard() {
  const { selectedOrg } = useOrganization();
  const organization = selectedOrg as ExtendedOrganization | null;
  const [metrics, setMetrics] = useState({
    total_segments: 0,
    active_propositions: 0,
    avg_obvious_choice_score: 0,
    total_pipeline_value: 0,
  });
  const [loading, setLoading] = useState(true);
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!organization) return;
    setLoading(true);
    axios
      .get(
        `${API_URL}/analytics/dashboard?organization_id=${organization.id}`,
        {
          headers: getAuthHeaders(),
        },
      )
      .then((res) => {
        setMetrics(res.data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [organization]);

  const currencyCode = organization?.currency || 'USD';

  const metricsDisplay = [
    {
      label: 'Total Segments',
      value: metrics.total_segments,
      icon: ChartBarIcon,
      color: 'bg-blue-500',
    },
    {
      label: 'Active Propositions',
      value: metrics.active_propositions,
      icon: CubeIcon,
      color: 'bg-purple-500',
    },
    {
      label: 'Avg Obvious Choice Score',
      value: metrics.avg_obvious_choice_score.toFixed(1),
      icon: ScaleIcon,
      color: 'bg-green-500',
    },
    {
      label: 'Total Pipeline Value',
      value: formatCurrency(metrics.total_pipeline_value, currencyCode, {
        compact: true,
      }),
      icon: BanknotesIcon,
      color: 'bg-yellow-500',
    },
  ];

  const hasData = metrics.total_segments > 0 || metrics.active_propositions > 0;

  const truncateText = (text: string, wordLimit = 25) => {
    const words = text.split(' ');
    if (words.length <= wordLimit) return text;
    return words.slice(0, wordLimit).join(' ') + '...';
  };

  const shouldShowExpandButton =
    organization?.description &&
    organization.description.split(' ').length > 25;

  return (
    <div className="p-6">
      <div className="bg-white p-4 rounded-lg shadow-md mb-6 border-l-4 border-blue-500">
        <h1 className="text-2xl font-bold text-gray-800">
          Welcome to {organization?.name || 'your organization'}&apos;s Strategy
          Platform
        </h1>
        <p className="text-gray-600 mt-2">
          This dashboard shows your overall strategic position across all
          segments and propositions.
        </p>
        {organization?.description && (
          <div className="mt-2 text-gray-500 italic">
            <p>
              {descriptionExpanded
                ? organization.description
                : truncateText(organization.description)}
              {shouldShowExpandButton && (
                <button
                  className="text-blue-500 hover:underline ml-2"
                  onClick={() => setDescriptionExpanded(!descriptionExpanded)}
                >
                  {descriptionExpanded ? 'Show less' : 'Show more'}
                </button>
              )}
            </p>
          </div>
        )}
        {organization?.geographic_markets &&
          organization.geographic_markets.length > 0 && (
            <p className="text-sm text-gray-500 mt-2">
              Operating in: {organization.geographic_markets.join(', ')}
            </p>
          )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        <ClickableMetric
          value={loading ? 0 : metrics.total_segments}
          label="Total Segments"
          icon={<ChartBarIcon className="h-8 w-8 text-blue-500" />}
          color="text-gray-800"
          bgColor="bg-white"
          organizationId={organization?.id || ''}
          metricType="segments"
          className="shadow-md"
        />
        <ClickableMetric
          value={loading ? 0 : metrics.active_propositions}
          label="Active Propositions"
          icon={<CubeIcon className="h-8 w-8 text-purple-500" />}
          color="text-gray-800"
          bgColor="bg-white"
          organizationId={organization?.id || ''}
          metricType="propositions"
          className="shadow-md"
        />
        {metricsDisplay.slice(2).map((m) => (
          <div
            key={m.label}
            className="bg-white p-6 rounded-lg shadow-md flex items-center"
          >
            <div className={`p-3 rounded-full text-white ${m.color}`}>
              <m.icon className="h-8 w-8" />
            </div>
            <div className="ml-4">
              <p className="text-sm text-gray-500">{m.label}</p>
              <p className="text-2xl font-bold text-gray-800">
                {loading ? '...' : m.value}
              </p>
            </div>
          </div>
        ))}
      </div>

      {!hasData ? (
        <div className="bg-white p-8 rounded-lg shadow-md text-center">
          <h2 className="text-xl font-bold text-gray-800">
            Get Started with Your Strategy Platform
          </h2>
          <p className="text-gray-600 mt-2">
            Start building your strategic position by defining your market
            segments and value propositions.
          </p>
          <div className="mt-6 flex justify-center gap-4">
            <button
              className="flex items-center px-6 py-3 text-white bg-blue-600 rounded-lg hover:bg-blue-700"
              onClick={() => navigate('/segments')}
            >
              <PlusCircleIcon className="h-5 w-5 mr-2" />
              Create Your First Segment
            </button>
            <button
              className="px-6 py-3 text-blue-600 bg-white border border-blue-600 rounded-lg hover:bg-blue-50"
              onClick={() => navigate('/propositions')}
            >
              Define Value Propositions
            </button>
          </div>
        </div>
      ) : (
        <div>
          <div className="bg-blue-100 border-l-4 border-blue-500 text-blue-700 p-4 rounded-lg mb-6">
            <div className="flex">
              <div className="py-1">
                <InformationCircleIcon className="h-5 w-5 mr-3" />
              </div>
              <div>
                <p className="font-bold">Next Steps</p>
                <p className="text-sm">
                  Add companies to your segments and score your propositions to
                  see detailed analytics and recommendations.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-lg font-bold text-gray-800">Quick Actions</h3>
            <div className="mt-4 flex gap-4 flex-wrap">
              <button
                className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
                onClick={() => navigate('/segments')}
              >
                Manage Segments
              </button>
              <button
                className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
                onClick={() => navigate('/propositions')}
              >
                Review Propositions
              </button>
              <button
                className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
                onClick={() => navigate('/companies')}
              >
                Add Companies
              </button>
              <button
                className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
                onClick={() => navigate('/positioning')}
              >
                Score Matrix
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
